import { PageType as PLPPageType, PLPURL } from "@tvg/product-listing-url";
import { getCrumbHref } from "common/utils/seo/seoBreadcrumbHref";
import { getUnfilteredFacetName } from "common/utils/seo/seo-title";
import { Category, FacetList, SearchAPIMetadata } from "types/search-api";

export type BreadcrumbLink = {
  text: string;
  href: string;
};

export type PageType = "browse" | "brand" | "promo" | "search";

// structurally minimal on purpose: browse/promo callers feed the full search response, the search
// route feeds a slice of one - both satisfy this without either caller needing a cast
export interface BreadcrumbSource {
  meta?: {
    parent_categories?: Category[];
    selected_category?: Category;
    collection?: { id?: string; display_name?: string };
    request?: { browse_filter_name?: string } | null;
    result_id?: string;
  };
  facet_list?: FacetList[];
}

type ValidCategory = { group_id: string; display_name: string };

const SLUG_RE = /^[a-z0-9-]+$/;
const MAX_PARENT_CATEGORIES = 10;
// the term stays visible after the taxonomy - single flip back if requirements move again
const SEARCH_TERM_AFTER_TAXONOMY = true;

export const createBreadcrumbLinks = (url: PLPURL, response?: BreadcrumbSource | null): BreadcrumbLink[] => {
  const breadcrumbLinks: BreadcrumbLink[] = [
    {
      text: "Home",
      href: "/",
    },
  ];
  const pageType = getPageType(url, response);
  switch (pageType) {
    case "brand": {
      breadcrumbLinks.push(buildBrandBreadcrumbLink(url, response));
      return breadcrumbLinks;
    }
    case "browse": {
      breadcrumbLinks.push(...buildTaxonomyLinks(url, response));
      break;
    }
    case "promo": {
      breadcrumbLinks.push(...buildPromoLinks(url, response));
      break;
    }
    case "search": {
      breadcrumbLinks.push(...buildSearchLinks(url, response));
      break;
    }
    default: {
      return breadcrumbLinks;
    }
  }

  if (breadcrumbLinks.length < 2) {
    return [];
  }

  // last crumb is the current page - it needs the real URL for the component's JSON-LD, not
  // whichever browse/promo link the taxonomy would otherwise give it
  breadcrumbLinks[breadcrumbLinks.length - 1].href = url.pathname;
  return breadcrumbLinks;
};

const getPageType = (url: PLPURL, response?: BreadcrumbSource | null): PageType | undefined => {
  if (isBrandStorePage(url, response)) {
    return "brand";
  }

  switch (url.type) {
    case PLPPageType.Browse:
      return "browse";
    case PLPPageType.Promo:
      return "promo";
    case PLPPageType.Search:
      return "search";
    default:
      return undefined;
  }
};

const buildBrandBreadcrumbLink = (url: PLPURL, response?: BreadcrumbSource | null): BreadcrumbLink => {
  // getUnfilteredFacetName wants the full SearchAPIMetadata shape; the brand case only fires for
  // browse responses, which always carry it in full - BreadcrumbSource stays structural for the
  // other callers, which never reach this branch
  const brandName = response
    ? getUnfilteredFacetName(
        response.meta as unknown as SearchAPIMetadata,
        response.facet_list ?? [],
        url.unfilteredFacet!,
      )
    : "";

  return {
    href: url.pathname,
    text: brandName,
  };
};

const isBrandStorePage = (url: PLPURL, response?: BreadcrumbSource | null): boolean => {
  // this will be null
  // pages like /browse/technology/brand--sony  will have `browse_filter_name = 'group_id'
  // and brand store pages have an unfiltered facet of brand
  if (response?.meta?.request?.browse_filter_name === "brand" && url.unfilteredFacet === "brand") {
    return true;
  }

  return false;
};

const buildTaxonomyLinks = (url: PLPURL, response?: BreadcrumbSource | null): BreadcrumbLink[] => {
  if (!url.category?.id) {
    return [];
  }

  return getTaxonomyCategories(response).map((category, index) => ({
    text: category.display_name,
    href: getCrumbHref(category.group_id, index === 0),
  }));
};

const buildPromoLinks = (url: PLPURL, response?: BreadcrumbSource | null): BreadcrumbLink[] => {
  const collectionName = response?.meta?.collection?.display_name;
  if (typeof collectionName !== "string" || !collectionName) {
    return [];
  }

  const collectionCrumb: BreadcrumbLink = {
    text: collectionName,
    href: url.pageName ? `/promo/${url.pageName}` : url.pathname,
  };

  return [...buildTaxonomyLinks(url, response), collectionCrumb];
};

const buildSearchLinks = (url: PLPURL, response?: BreadcrumbSource | null): BreadcrumbLink[] => {
  const taxonomy = buildTaxonomyLinks(url, response);
  if (taxonomy.length) {
    return SEARCH_TERM_AFTER_TAXONOMY && url.searchTerm
      ? [...taxonomy, { text: url.searchTerm, href: url.pathname }]
      : taxonomy;
  }

  return url.searchTerm ? [{ text: url.searchTerm, href: url.pathname }] : [];
};

const getTaxonomyCategories = (response?: BreadcrumbSource | null): ValidCategory[] => {
  const rawParents = response?.meta?.parent_categories;
  const parents =
    Array.isArray(rawParents) && rawParents.length <= MAX_PARENT_CATEGORIES
      ? rawParents.map(toValidCategory).filter(isValidCategory)
      : [];

  const selected = toValidCategory(response?.meta?.selected_category);
  return selected ? [...parents, selected] : parents;
};

// meta arrives from a page-global event bus on the client with no validation, so a category is only
// trusted when both fields are present strings and the id looks like a real slug
const toValidCategory = (category: unknown): ValidCategory | null => {
  if (typeof category !== "object" || category === null) {
    return null;
  }

  const { group_id: groupId, display_name: displayName } = category as Category;
  if (typeof displayName !== "string" || !displayName) {
    return null;
  }
  if (typeof groupId !== "string" || groupId === "all" || !SLUG_RE.test(groupId)) {
    return null;
  }

  return { group_id: groupId, display_name: displayName };
};

const isValidCategory = (category: ValidCategory | null): category is ValidCategory => category !== null;
